import React, { useState, SyntheticEvent } from 'react';
import { debounce } from 'lodash';
import { Table } from '../../../../../../core/components/table';
import type { Item } from '../../../../../../core/components/toggle';
import { MultiToggle } from '../../../../../../core/components/toggle';
import { ExperimentConfiguration, TissueMagneticProps } from '../../../../models';
import simulatorConfig from '../../../../constants/simulator-config-constants';
import type { ModalProps } from '../patient-settings-modal';
import { generateColumns, COLUMN_KEYS } from './generate-columns';
import type { TissueMagneticPropertyMode } from './use-tissue-magnetic-properties-mode';
import { useTissueMagneticPropertiesMode } from './use-tissue-magnetic-properties-mode';
import { ParagraphMedium, SmallSecondaryButton } from '../../../../../../core/components';
import { ModalSectionContainer } from '../../modal-section-container';

export type TissueMagneticPropertiesSectionProps = Pick<
  ModalProps,
  'config' | 'onSubmitCell' | 'resetTissueCharacteristics' | 'tissueCharacteristics'
> & { setUseCustomTissueMagneticProperties: ModalProps['updatePatientSettings'] };

const TISSUE_MAGNETIC_PROPERTY_MODE_OPTIONS: Item<TissueMagneticPropertyMode>[] = [
  {
    id: 'default',
    label: 'Default',
  },
  {
    id: 'advanced',
    label: 'Advanced',
  },
];

export const TissueMagneticPropertiesSection = ({
  config,
  onSubmitCell,
  resetTissueCharacteristics,
  tissueCharacteristics,
  setUseCustomTissueMagneticProperties,
}: TissueMagneticPropertiesSectionProps): JSX.Element => {
  const [toggleEditCell, setToggleEdit] = useState({ toggleEdit: true, key: '', index: -1 });
  const [changedCell, setChangedCell] = useState({
    type: '',
    t1: 0,
    t2: 0,
  });

  const customTissueMagneticPropertiesConfiguration = config?.find(
    (c: ExperimentConfiguration) => c.name === simulatorConfig.CUSTOM_TISSUE_MAGNETIC_PROPERTIES
  );

  const { tissueMagneticPropertyMode, onTissueMagneticPropertyModeChange } = useTissueMagneticPropertiesMode({
    customTissueMagneticPropertiesConfiguration,
    setUseCustomTissueMagneticProperties,
  });

  const inputChangeHandler = (e: SyntheticEvent, record: TissueMagneticProps, key: string | number) => {
    // extract new tissue value
    const newValue = (e.target as HTMLInputElement).value;
    setChangedCell({
      type: record.type,
      t1: key === COLUMN_KEYS.t1 ? Number(newValue) : record.t1,
      t2: key === COLUMN_KEYS.t2 ? Number(newValue) : record.t2,
    });
  };

  const inputChangeHandlerDebounced = debounce(inputChangeHandler, 100);

  const onToggleEditHandler = (key: string, index: number) => {
    setToggleEdit({ toggleEdit: !toggleEditCell.toggleEdit, key, index });
  };

  const submitCellHandler = () => {
    onSubmitCell(changedCell.type, changedCell.t1, changedCell.t2);

    // reset form input
    setToggleEdit({ toggleEdit: true, key: '', index: -1 });
  };

  const columns = generateColumns({
    onChange: inputChangeHandlerDebounced,
    onToggleEdit: onToggleEditHandler,
    onSubmitCell: submitCellHandler,
    toggleEditCell,
  });

  return (
    <ModalSectionContainer title="Tissue magnetic properties">
      <div className="mb-4">
        <ParagraphMedium>
          Using default settings is recommended. The tissue magnetic properties are then adjusted automatically
          according to the current magnetic field configuration. Enabling advanced settings lets you manually control
          the magnetic properties of different tissue types.
        </ParagraphMedium>

        <MultiToggle
          items={TISSUE_MAGNETIC_PROPERTY_MODE_OPTIONS}
          onSelect={(key) => onTissueMagneticPropertyModeChange(key as TissueMagneticPropertyMode)}
          selectedItemKey={tissueMagneticPropertyMode}
        />
      </div>

      {tissueMagneticPropertyMode === 'advanced' && (
        <>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="settings-modal__control-label mt-0">Advanced properties</span>
            <SmallSecondaryButton className="mb-n7 justify-self-end" onClick={resetTissueCharacteristics}>
              Reset tissue characteristics
            </SmallSecondaryButton>
          </div>
          <Table onSelectAll={() => null} columns={columns} data={tissueCharacteristics} />
        </>
      )}
    </ModalSectionContainer>
  );
};
